import React, { useEffect, useState, useContext, createContext } from "react";

import { useAppContext } from "../utils/context/app-context";
import {
	ContentContainer,
	TitleHeader,
	TableLoading,
	MaterialIcon,
	Button,
	AlertMessage
} from "../components/UiComponents";
import {
	CourseHeader,
	CourseWrapper,
} from "../components/CourseComponents";
import useDocumentTitle from "../components/useDocumentTitle";



const CertificateContext = createContext();

const CertificatesPage = () => {
	const { dispatch, api, state } = useAppContext();
	const isOffline = state.offline;
	useDocumentTitle("Certificates");
	const [loading, setLoading] = useState(true);
	const [certificatesSending, setCertificatesSending] = useState([]);
	const [certificateIdsSent, setCertificateIdsSent] = useState([]);
	const [sendSuccess, setSendSuccess] = useState(false);
	const [certificateSendingError, setCertificateSendingError] = useState(null);
	let course = api.getCurrentCourse();

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		if (sendSuccess) {
			setTimeout(() => {
				setSendSuccess(false);
				setCertificateIdsSent([]);
			}, 3000);
		}
	}, [sendSuccess]);


	async function fetchData() {
		if (isOffline) {
			setLoading(false);
			return;
		}
		const { data } = await api.fetchData(
			`/courses/${course.id}/students`
		);
		console.log(course)
		if (data?.students) {
			dispatch({
				type: "SET_COURSE_STUDENTS",
				payload: {
					students: data.students,
					course_id: course.id,
				},
			});
			setLoading(false);
		} else {
			setLoading(false);
		}
	}

	async function sendCertificates(studentId = null) {
		let studentIds = [];
		if (!studentId) {
			studentIds = course.students.filter((student) => student.completed_course).map((student) => student.id);
		} else {
			studentIds = [studentId];
		}

		setCertificatesSending(studentIds);
		setCertificateSendingError(null);
		setSendSuccess(false);

		const postData = {
			course_id: course.id,
			student_ids: studentIds,
		};

		const { data, error } = await api.fetchData(`/certificates/send`, 'POST', postData);

		if (data?.success) {
			dispatch({
				type: "SET_STUDENT_CERTIFICATES_SENT",
				payload: {
					course_id: course.id,
					students: studentIds,
				},
			});
			setCertificatesSending([]);
			setCertificateIdsSent(studentIds);
			setSendSuccess(true);
		} else {
			let errorMessage = '';
			// If error is a string, set it as the error message
			// Otherwise, set a generic error message
			if (data?.error) {
				errorMessage = data.error;
			} else if (typeof error !== "string") {
				errorMessage = "An error occurred while sending certificates.";
			}
			setCertificateSendingError(errorMessage);
			setCertificatesSending([]);
		}
	}

	const HeaderButtons = () => {
		return (
			<div className="attendance-register-page__action-buttons">
				<Button
					text={"Send All Certificates"}
					type="outline-success"
					size="sm"
					onClick={() => sendCertificates()}
					disabled={isOffline}
				/>
			</div>
		);
	};

	const contextData = {
		certificatesSending,
		certificateIdsSent,
		sendCertificates,
	};

	return (
		<div className="certificates-page">
			<CourseHeader />
			<ContentContainer classes="mb-4">
				<CourseWrapper>
					<TitleHeader
						title={`Certificates`}
						rightContent={loading ? null : <HeaderButtons />}
					/>
					<div className="certificates">
						{loading ? (
							<TableLoading rows={5} />
						) : (
							<CertificateContext.Provider value={contextData}>
								{course.students.length === 0 ? (
									<div className="certificates__empty">
										<p>No students have registered yet.</p>
									</div>
								) : (
									<>
										{certificateSendingError && (
											<div className="mb-3">
												<AlertMessage type="danger" message={certificateSendingError} />
											</div>
										)}
										{sendSuccess && (
											<div className="mb-3">
												<AlertMessage type="success" message="Certificates sent successfully." />
											</div>
										)}
										<StudentsTable students={course.students} />
									</>
								)}
							</CertificateContext.Provider>
						)}
					</div>
				</CourseWrapper>
			</ContentContainer>
		</div>
	);
};

const StudentsTable = ({ students }) => {
	if (students.length === 0) {
		return null;
	}
	return (
		<table className="table table-striped responsive-table students-table">
			<thead>
				<tr>
					<th>Name</th>
					<th>Email</th>
					<th className="text-center">Passed Course</th>
					<th className="text-center">Certificate Sent</th>
					<th></th>
				</tr>
			</thead>
			<tbody>
				{students.map((student) => (
					<StudentsTableRow student={student} key={student.id} />
				))}
			</tbody>
		</table>
	);
};

const StudentsTableRow = ({ student }) => {
	const { state } = useAppContext();
	const isOffline = state.offline;
	const { sendCertificates, certificatesSending, certificateIdsSent } = useContext(CertificateContext);
	const studentId = student.id;
	const passed = student?.completed_course;
	const sent = student?.certificate_sent_date;

	const isSending = certificatesSending.includes(studentId);
	const isSent = certificateIdsSent.includes(studentId);

	return (
		<tr>
			<td>
				{student.first_name} {student.last_name}
			</td>
			<td>{student.email}</td>
			<td className="text-center" data-label="Passed Course">
				<MaterialIcon
					icon={passed ? "check" : "close"}
					className={`text-${passed ? "success" : "danger"
						}`}
				/>
			</td>
			<td className="text-center" data-label="Certificate Sent">
				{passed && (
					<MaterialIcon
						icon={sent ? "check" : "close"}
						className={`text-${sent ? "success" : "danger"
							}`}
					/>
				)}
			</td>
			<td className="text-center">
				{passed && (
					<Button
						text={isSending ? "Sending..." : isSent ? "Sent!" : "Send"}
						disabled={isSending || isSent || isOffline}
						type="btn btn-sm btn-success"
						size="sm"
						onClick={() => sendCertificates(studentId)}
					/>
				)}
			</td>
		</tr>
	);
};

export default CertificatesPage;
